import React, { useContext, useState } from 'react';
import {
  IHistoryLtv,
  useCreateLtvMutation,
  useUpdateLtvMutation
} from '../../../../generated/graphql';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { parseDate } from '../../../../utils/clientUtils';
import DialogBox from '../../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import InputComponent from '../../../Common/Fields/InputComponent';
import { Colors } from '../../../../utils/theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, ltvInput?: IHistoryLtv) => void;
  ltvInput: IHistoryLtv | null;
}

type IErrors = {
  value: string | null;
  dateStart: string | null;
};

const defaultErrorsObj: IErrors = {
  value: null,
  dateStart: null
};

const errorTexts = {
  date: 'Date is required!',
  value: 'Ltv value is required!'
};

const LtvForm: React.FunctionComponent<IProps> = ({ stockId, ltvInput, handleClose }: IProps) => {
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [ltvValue, setLtvValue] = useState<number | null>(ltvInput?.value || null);
  const [dateStart, setDateStart] = useState<Date | null>(
    ltvInput ? parseDate(ltvInput.start_date) : new Date()
  );
  const [createLtv, createLtvResult] = useCreateLtvMutation();
  const [updateLtv, updateLtvResult] = useUpdateLtvMutation();
  const { dispatch, state } = useContext(AppContext);
  const [editable, setEditable] = useState(ltvInput === null);
  const canEdit = canUserEdit(state.user, Types.Stock);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.currentTarget.value === '' ? null : Number(event.currentTarget.value);
    if (value === null) {
      setFormErrors({ ...formErrors, value: errorTexts.value });
    } else {
      setFormErrors({ ...formErrors, value: null });
    }
    setLtvValue(value);
  };

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, dateStart: null });
      }
    } else {
      setFormErrors({ ...formErrors, dateStart: errorTexts.date });
    }
    setDateStart(input);
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      dateStart: null,
      value: null
    };
    if (!dateStart) {
      hasErrors = true;
      errors.dateStart = errorTexts.date;
    }
    if (!ltvValue) {
      hasErrors = true;
      errors.value = errorTexts.value;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(ltvInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createLtv({
        variables: {
          stock_id: stockId,
          input: {
            start_date: (dateStart as Date).toString(),
            value: ltvValue as number
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IHistoryLtv);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Ltv created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateLtv({
        variables: {
          id: ltvInput?.id as string,
          input: {
            start_date: (dateStart as Date).toString(),
            value: ltvValue as number
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IHistoryLtv);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Ltv updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  const handleCancel = () => {
    if (editable && ltvInput !== null) {
      setLtvValue(ltvInput?.value || null);
      setDateStart(parseDate(ltvInput.start_date));
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };
  return (
    <DialogBox
      id="ltv-dialog"
      width={410}
      title={ltvInput ? 'Ltv Details' : 'Create Ltv'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {ltvInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="ltv-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box display="flex" gap="15px" sx={{ my: '20px' }}>
        <InputComponent
          id="ltv-value"
          label="LTV Value"
          type="number"
          value={ltvValue}
          onChange={handleValueChange}
          disabled={createLtvResult.loading || updateLtvResult.loading || !editable}
          errorMsg={formErrors.value}
        />
        <Box sx={{ width: '100%' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="ltv-date-picker"
              label="Date"
              value={dateStart}
              disabled={createLtvResult.loading || updateLtvResult.loading || !editable}
              onChange={(d) => handleDateChange(d)}
              errorMsg={formErrors.dateStart}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-ltv-button"
          onClick={handleCancel}
          disabled={createLtvResult.loading || updateLtvResult.loading}
          text={ltvInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-ltv-button"
          data-testid="save-ltv-button"
          onClick={handleSubmit}
          disabled={createLtvResult.loading || updateLtvResult.loading}
          text={ltvInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default LtvForm;
